import { defineStore } from 'pinia'
import {
  ONBOARDING_RECORD_VERSION,
  OnboardingRecord,
  OnboardingStage,
  onboardingStorageKey,
  readOnboardingRecord,
  writeOnboardingRecord
} from '@/modules/global/onboardingStorage'

export type OnboardingState = {
  /** Scope the in-memory stage belongs to. Both null until an account is known. */
  userId: string | null
  teamId: string | null
  /** null means "no record" — which never forces onboarding, only initialization does. */
  stage: OnboardingStage | null
  sharedTemplateId: string | null
  fromRegisterFlow: boolean
}

/**
 * NS-310: reactive mirror of the persisted onboarding record (see onboardingStorage.ts).
 *
 * In-memory state is per account: `syncScope` drops it as soon as the user or team changes, and
 * `$reset` via AuthStore.clearAllStores wipes it on logout. The localStorage records deliberately
 * survive both, so signing back in resumes where the founder left off.
 */
export const useOnboardingStore = defineStore('onboarding', {
  state: (): OnboardingState => ({
    userId: null,
    teamId: null,
    stage: null,
    sharedTemplateId: null,
    fromRegisterFlow: false
  }),
  getters: {
    isRegisterFlowAnalytics(): boolean {
      return this.fromRegisterFlow && this.isPending
    },
    isPending(): boolean {
      return this.stage === OnboardingStage.WELCOME || this.stage === OnboardingStage.TEMPLATE_SELECTION
    },
    isWelcomeStage(): boolean {
      return this.stage === OnboardingStage.WELCOME
    },
    isTemplateSelectionStage(): boolean {
      return this.stage === OnboardingStage.TEMPLATE_SELECTION
    }
  },
  actions: {
    /**
     * Points the store at one account's record. Cheap and idempotent, so callers on hot paths (the
     * navigation guard) can call it on every navigation.
     */
    syncScope(userId?: string | null, teamId?: string | null): void {
      if (!userId || !teamId) {
        this.clearInMemoryState()
        return
      }
      if (this.userId === userId && this.teamId === teamId) {
        return
      }
      this.userId = userId
      this.teamId = teamId
      this.readFromStorage()
    },

    /**
     * Starts onboarding for any user who is in the setup flow but has not finished it yet — NS-353
     * includes invited members, not just founders. Never overwrites an existing record, so a reload
     * mid-flow cannot rewind the user to the welcome modal after they had already moved on.
     */
    initializeWelcome(userId?: string | null, teamId?: string | null, fromRegisterFlow = false): void {
      this.syncScope(userId, teamId)
      if (!this.userId || !this.teamId || this.stage) {
        return
      }
      this.fromRegisterFlow = fromRegisterFlow
      this.persist(OnboardingStage.WELCOME, null)
    },

    /**
     * Backfills the register-flow analytics flag on an existing pending record. Framework calls this
     * before dropping the URL query param so trackOnboardingFlow2/3 still fire later in the funnel.
     */
    captureRegisterFlowAnalytics(): void {
      const stage = this.stage
      if (!this.isPending || this.fromRegisterFlow || !stage) {
        return
      }
      this.fromRegisterFlow = true
      this.persist(stage, this.sharedTemplateId)
    },

    /** Moves a pending flow between WELCOME and TEMPLATE_SELECTION. No-op once onboarding is done. */
    saveStage(stage: OnboardingStage): void {
      if (!this.isPending) {
        return
      }
      this.persist(stage, this.sharedTemplateId)
    },

    /**
     * Records the template a shared-template signup link asked for, and skips the welcome modal —
     * that link already expressed the intent the modal exists to collect. Only the first sighting
     * advances the stage, so dismissing the picker afterwards still returns to welcome.
     */
    rememberSharedTemplate(templateId: string): void {
      if (!this.isPending || !templateId || this.sharedTemplateId === templateId) {
        return
      }
      this.persist(OnboardingStage.TEMPLATE_SELECTION, templateId)
    },

    /**
     * Marks onboarding done. Returns whether it actually completed a pending flow, so callers can
     * tell an onboarding creation apart from an ordinary one.
     */
    completeIfPending(): boolean {
      if (!this.isPending) {
        return false
      }
      this.persist(OnboardingStage.COMPLETED, this.sharedTemplateId)
      return true
    },

    /**
     * Picks up a change written by another tab (completion, most importantly). A null key means the
     * whole storage was cleared.
     */
    handleStorageEvent(event: Pick<StorageEvent, 'key'>): void {
      if (!this.userId || !this.teamId) {
        return
      }
      if (event.key && event.key !== onboardingStorageKey(this.userId, this.teamId)) {
        return
      }
      this.readFromStorage()
    },

    clearInMemoryState(): void {
      this.userId = null
      this.teamId = null
      this.stage = null
      this.sharedTemplateId = null
      this.fromRegisterFlow = false
    },

    readFromStorage(): void {
      if (!this.userId || !this.teamId) {
        return
      }
      const record = readOnboardingRecord(this.userId, this.teamId)
      this.stage = record?.stage ?? null
      this.sharedTemplateId = record?.sharedTemplateId ?? null
      this.fromRegisterFlow = record?.fromRegisterFlow === true
    },

    persist(stage: OnboardingStage, sharedTemplateId: string | null): void {
      this.stage = stage
      this.sharedTemplateId = sharedTemplateId
      if (!this.userId || !this.teamId) {
        return
      }
      const record: OnboardingRecord = {
        version: ONBOARDING_RECORD_VERSION,
        stage,
        sharedTemplateId
      }
      if (this.fromRegisterFlow) {
        record.fromRegisterFlow = true
      }
      writeOnboardingRecord(this.userId, this.teamId, record)
    }
  }
})

export type OnboardingStore = ReturnType<typeof useOnboardingStore>
